/**
 * The words of an agent's card (audits/2026-10-26/agent-control-spec.md, rev. 2). 🔴 Every
 * section says what it stops or allows, and on what condition: the controls govern the
 * advisor's proposals in the approval queue and no route reads them for the agent's own token;
 * pause and retire stop that token only once a service client is bound to the agent (console-kit
 * 0.5.0 refuses a bound agent that is not active), which no sentence may promise without saying so.
 */
import { microAmount } from "./money.ts";

export const CARD_ABOUT =
	"What this agent may propose, and up to what. Today these settings govern the advisor's proposals in the approval queue — what it may queue and what can be approved. They do not govern the agent's own calls: no route of this console reads them for its token.";

export const STATUS_TODAY =
	"Pause or retire stops this agent's proposals: none is taken into the queue, and none already waiting can be approved. It stops the agent's own token only once the agent is bound — a service client's subject set on the agent, with migration 0008 applied; until then, its own token is not stopped.";

/**
 * The subject, as console-kit 0.5.0 (tag v0.5.0, d371f8f) has it: «`subject` is the `sub` of the client that acts as
 * this agent» (routes/entities.ts:110), «the `sub` claim of the agent's client_credentials
 * token» (migrations/0008_agent_subject.sql:13). The one value named is the one measured:
 * svc-twads-agents' token carries `sub = user_svc_twads_agents` (audits/2026-10-26/
 * svc-claims-measured.md:14) — an example, not a rule about every client's `sub`, which the kit
 * does not make.
 */
export const SUBJECT_LABEL = "Service client (subject)";

export const SUBJECT_HELP =
	"The sub of the machine client that acts as this agent — the sub claim of its client_credentials token; svc-twads-agents' token carries user_svc_twads_agents. Empty unbinds the client.";

export const SUBJECT_NONE = "No service client is bound to this agent.";

/** The kit's 409 (routes/entities.ts:122): «a subject that is another agent's: the caller can pick another or unbind the first». */
export const SUBJECT_TAKEN =
	"This subject is already bound to another agent: choose another, or unbind it from that agent first.";

/** The kit's 503 before migration 0008 (routes/entities.ts:123–124); its `unavailable` names the file. */
export const SUBJECT_UNAVAILABLE = "The subject cannot be written yet:";

/** The card reads the subject from the agents list; one read returns at most 1000 (Agents.tsx). */
export const SUBJECT_NOT_READ =
	"This agent's subject was not read: it is not among the agents one read returns, so it is not shown or changed here.";

/** The body the kit's PATCH takes: the trimmed text, or `null` — the kit's «`null` unbinds the client». */
export function subjectBody(draft: string): { subject: string | null } {
	const subject = draft.trim();
	return { subject: subject === "" ? null : subject };
}

/** What the card says after the PATCH, by its status and body. */
export function subjectAnswerText(
	status: number,
	body: unknown,
): { text: string; failed: boolean } {
	const b = (body ?? {}) as {
		agent?: { subject?: string | null };
		error?: string;
		unavailable?: string;
	};
	if (status >= 200 && status < 300)
		return {
			text: b.agent?.subject
				? `Subject saved: ${b.agent.subject}.`
				: `Subject cleared. ${SUBJECT_NONE}`,
			failed: false,
		};
	if (status === 409) return { text: SUBJECT_TAKEN, failed: true };
	if (status === 503)
		return {
			text: `${SUBJECT_UNAVAILABLE} ${b.unavailable ?? b.error ?? `HTTP ${status}`}`,
			failed: true,
		};
	return { text: b.error ?? `HTTP ${status}`, failed: true };
}

export const LIMITS_HELP =
	"Per object, in euros: a daily budget up to the daily figure, a total up to the total. Empty is «not configured», and the console then refuses every budget this agent proposes — never «unlimited». These figures only narrow the platform's own cap; a figure over it is refused.";

export const LEVELS_HELP =
	"«never» refuses the action outright. «self» and «propose» both put the action in the approval queue: «self» is not built here, so the agent writes nothing itself. What the autonomy draft calls «never» cannot be changed here.";

export const SCOPE_HELP =
	"The accounts this agent may propose for. «write» lets it queue changes there; «read» only lets it read. Campaign ids, comma-separated, narrow it to those campaigns; empty is every campaign of the account, and a new campaign is outside any list.";

export const UNAVAILABLE = "The agent controls are not set up yet.";

/** A cap in euro micro units as an amount the field shows, or empty for «not configured». */
export function capText(micro: number | null): string {
	return micro === null ? "" : microAmount(micro); // the arithmetic is money.ts's, not this file's
}

/** The platform's cap in words: its figure in euros, «not configured», or what is wrong with it. */
export function platformCapWords(cap: {
	daily: number | null;
	total: number | null;
	misconfigured: string[];
}): string {
	if (cap.misconfigured.length > 0)
		return `The platform's cap is misconfigured: ${cap.misconfigured.join("; ")}`;
	const one = (label: string, v: number | null) =>
		`${label} ${v === null ? "not configured" : `${capText(v)} €`}`;
	return `The platform's cap: ${one("daily", cap.daily)}, ${one("total", cap.total)}. An agent's daily or total budget is refused while the platform has no figure for it.`;
}

/** «a, b» from a comma-separated field; empty is null — every campaign. */
export function campaignIdsOf(text: string): string[] | null {
	const ids = text
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
	return ids.length === 0 ? null : ids;
}
